import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { OAuthServiceType } from "@common/constants";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { OAuthAuthorizationResponse, triggerAuthorization } from "@lib/oauth";
import { useWindowMessage } from "@hooks/useWindowMessage";
import { useOrgIntegrationReconnectActions } from "@api/organizationIntegrationReconnect.api";
import { usePayPalAuthorization } from "@components/paypal/usePayPalAuthorization";
import {
    ORG_RECONNECT_OAUTH_SCOPES,
    OrgReconnectStrategy,
    orgReconnectStrategy,
} from "./orgReconnectStrategy";
import {
    OrgReconnectContext,
    OrgReconnectContextValue,
} from "./OrgReconnectContext";

interface PendingReconnect {
    item: OrganizationIntegrationAccountItem;
    strategy: Extract<OrgReconnectStrategy, { kind: "oauthPopup" | "paypal" }>;
}

const RECONNECT_FAILED = "Couldn't reconnect. Please try again.";

/** PayPal posts back through its own SDK rather than an `OAuthServiceType`. */
const PAYPAL_PENDING_KEY = "paypal";

/**
 * Drives reconnects for every row on the page.
 *
 * Deliberately one host rather than per-row hooks. `useWindowMessage` has to be
 * mounted before the OAuth popup posts back and hooks cannot be called
 * conditionally, so a row-level hook would mount a listener for every scope on
 * every row. Group children also refetch while a popup is open, so pending
 * targets live in a ref here instead of row state that can go stale or unmount
 * mid-flight.
 *
 * Pending targets are keyed by the channel that will post back — the OAuth scope,
 * or PayPal's SDK. Two reconnects can be in flight on different providers, and
 * keying this way means each auth code is matched to the row that actually asked
 * for it rather than to whichever row happened to be clicked last.
 */
export const OrgIntegrationReconnectProvider = ({
    organizationId,
    children,
}: {
    organizationId: string;
    children: ReactNode;
}) => {
    const { toast, error: toastError } = useToaster();
    const actions = useOrgIntegrationReconnectActions(organizationId);
    const pendingByKeyRef = useRef(new Map<string, PendingReconnect>());
    const [reconnectingConnectionIds, setReconnectingConnectionIds] = useState<
        ReadonlySet<string>
    >(() => new Set());

    const markPending = useCallback((connectionId: string) => {
        setReconnectingConnectionIds(
            (previous) => new Set([...previous, connectionId]),
        );
    }, []);

    /**
     * Single reset path for every outcome — success, server rejection, or a row
     * whose workspace could not be resolved.
     */
    const clearPending = useCallback(
        ({ key, connectionId }: { key?: string; connectionId: string }) => {
            if (key !== undefined) {
                pendingByKeyRef.current.delete(key);
            }
            setReconnectingConnectionIds((previous) => {
                const next = new Set(previous);
                next.delete(connectionId);
                return next;
            });
        },
        [],
    );

    /**
     * Resolves whichever fix endpoint the posting channel's pending row needs.
     * Reads the ref rather than a captured value: the popup can post back long
     * after the click, by which time the row may have re-rendered.
     */
    const completeWithCode = useCallback(
        async ({ key, code }: { key: string; code: string }) => {
            const pending = pendingByKeyRef.current.get(key);
            if (!pending || pending.strategy.kind !== "oauthPopup") {
                return;
            }
            const { item, strategy } = pending;
            const { id: connectionId, providerConnectionId } = item.connection;
            const workspaceId = item.account.entity?.workspaceId;

            if (strategy.target === "native" && !workspaceId) {
                clearPending({ key, connectionId });
                toastError(RECONNECT_FAILED);
                return;
            }

            try {
                if (strategy.target === "native" && workspaceId) {
                    await actions.reconnectNative({
                        workspaceId,
                        connectionId: providerConnectionId,
                        oAuthCode: code,
                    });
                } else if (strategy.target === "stripe") {
                    // Legacy `stripe_connection` rows carry numeric ids; the
                    // integrations-framework rows are UUIDs and reconnect
                    // through the workspace-scoped native fix endpoint.
                    const isLegacyStripeConnection = /^\d+$/.test(
                        providerConnectionId,
                    );
                    if (isLegacyStripeConnection) {
                        await actions.reconnectStripe({
                            connectionId: Number(providerConnectionId),
                            code,
                        });
                    } else if (workspaceId) {
                        await actions.reconnectNative({
                            workspaceId,
                            connectionId: providerConnectionId,
                            oAuthCode: code,
                        });
                    } else {
                        clearPending({ key, connectionId });
                        toastError(RECONNECT_FAILED);
                        return;
                    }
                } else {
                    await actions.reconnectRamp({
                        connectionId: Number(providerConnectionId),
                        code,
                    });
                }
                clearPending({ key, connectionId });
                toast("Connection reconnected");
            } catch {
                // The interceptor already toasted the server's message, which is
                // more specific than anything this could add.
                clearPending({ key, connectionId });
            }
        },
        [actions, clearPending, toast, toastError],
    );

    const handlePayPal = useCallback(
        async ({
            authCode,
            sharedId,
        }: {
            authCode: string;
            sharedId: string;
        }) => {
            const pending = pendingByKeyRef.current.get(PAYPAL_PENDING_KEY);
            if (!pending || pending.strategy.kind !== "paypal") {
                return;
            }
            const { id: connectionId, providerConnectionId } =
                pending.item.connection;
            const workspaceId = pending.item.account.entity?.workspaceId;

            if (!workspaceId) {
                clearPending({ key: PAYPAL_PENDING_KEY, connectionId });
                toastError(RECONNECT_FAILED);
                return;
            }

            try {
                await actions.reconnectNative({
                    workspaceId,
                    connectionId: providerConnectionId,
                    customParams: { authCode, sharedId },
                });
                clearPending({ key: PAYPAL_PENDING_KEY, connectionId });
                toast("Connection reconnected");
            } catch {
                clearPending({ key: PAYPAL_PENDING_KEY, connectionId });
            }
        },
        [actions, clearPending, toast, toastError],
    );

    // Stable: `usePayPalAuthorization` keys its message listener on this
    // callback, so an inline arrow would tear it down and re-add it every render.
    const onPayPalAuthorized = useCallback(
        (authCode: string, sharedId: string) => {
            void handlePayPal({ authCode, sharedId });
        },
        [handlePayPal],
    );

    const { initializeConnection } = usePayPalAuthorization(onPayPalAuthorized);

    const reconnect = useCallback(
        async (item: OrganizationIntegrationAccountItem) => {
            const strategy = orgReconnectStrategy(item.connection.provider);
            if (strategy.kind === "unsupported") {
                return;
            }

            const connectionId = item.connection.id;
            markPending(connectionId);

            if (strategy.kind === "plaid") {
                // Link runs inline, so there is no popup round-trip to park.
                try {
                    const didReconnect = await actions.reconnectPlaid(
                        Number(item.connection.providerConnectionId),
                    );
                    clearPending({ connectionId });
                    if (didReconnect) {
                        toast("Connection reconnected");
                    }
                } catch {
                    clearPending({ connectionId });
                    toastError(RECONNECT_FAILED);
                }
                return;
            }

            const key =
                strategy.kind === "paypal"
                    ? PAYPAL_PENDING_KEY
                    : strategy.scope;

            // Replaces any earlier pending row on this channel, so clicking
            // Reconnect again after abandoning a popup restarts cleanly. There is
            // no close or timeout event to observe on `window.open`, so retry is
            // what recovers an abandoned handshake.
            pendingByKeyRef.current.set(key, { item, strategy });

            if (strategy.kind === "paypal") {
                initializeConnection();
                return;
            }

            triggerAuthorization(strategy.scope);
        },
        [
            actions,
            markPending,
            clearPending,
            toast,
            toastError,
            initializeConnection,
        ],
    );

    const value: OrgReconnectContextValue = useMemo(
        () => ({ reconnect, reconnectingConnectionIds }),
        [reconnect, reconnectingConnectionIds],
    );

    return (
        <OrgReconnectContext.Provider value={value}>
            {ORG_RECONNECT_OAUTH_SCOPES.map((scope) => (
                <OAuthCodeListener
                    key={scope}
                    scope={scope}
                    onCode={completeWithCode}
                />
            ))}
            {children}
        </OrgReconnectContext.Provider>
    );
};

/**
 * One `useWindowMessage` per scope. A component rather than a loop of hooks —
 * hook counts must stay stable across renders. The scope is passed back out so
 * the code is matched to the row that opened *this* channel.
 */
const OAuthCodeListener = ({
    scope,
    onCode,
}: {
    scope: OAuthServiceType;
    onCode: (params: { key: string; code: string }) => void;
}) => {
    useWindowMessage<OAuthAuthorizationResponse>({
        scope,
        handler: ({ code }) => onCode({ key: scope, code }),
    });
    return null;
};
